/**
 * Numeric Input Component
 *
 * Text input that only accepts numeric values with single decimal point.
 * No spinner arrows, mobile-optimized keyboard.
 */

import { forwardRef, type InputHTMLAttributes } from 'react'

import { clsx } from 'clsx'

export interface NumericInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'size'> {
  /** Input size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Whether the input has an error */
  hasError?: boolean
  /** Allow negative numbers */
  allowNegative?: boolean
  /** Maximum decimal places */
  decimalPlaces?: number
  /** Hide validation message */
  hideMessage?: boolean
}

function buildNumericKeyDownHandler(
  allowNegative: boolean,
  outerOnKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
): (e: React.KeyboardEvent<HTMLInputElement>) => void {
  return (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'e' || e.key === 'E' || e.key === '+') { e.preventDefault(); return }
    if (e.key === '-') {
      if (!allowNegative) { e.preventDefault(); return }
      const input = e.currentTarget
      if (input.selectionStart !== 0) { e.preventDefault(); return }
    }
    if (e.key === '.') {
      if (e.currentTarget.value.includes('.')) { e.preventDefault(); return }
    }
    outerOnKeyDown?.(e)
  }
}

function sanitizeNumericValue(value: string, allowNegative: boolean, decimalPlaces: number | undefined): string {
  let v = value.replace(/[^0-9.-]/g, '')
  const parts = v.split('.')
  if (parts.length > 2) { v = `${parts[0]}.${parts.slice(1).join('')}` }
  if (allowNegative) {
    /* v8 ignore next */
    const negCount = (v.match(/-/g) ?? []).length
    if (negCount > 1) { v = `-${v.replace(/-/g, '')}` }
    else if (v.indexOf('-') > 0) { v = v.replace('-', '') }
  } else {
    v = v.replace(/-/g, '')
  }
  if (decimalPlaces !== undefined && v.includes('.')) {
    const [integer, decimal] = v.split('.')
    /* v8 ignore next */
    v = `${integer}.${(decimal ?? '').slice(0, decimalPlaces)}`
  }
  return v
}

function buildNumericChangeHandler(
  allowNegative: boolean, decimalPlaces: number | undefined,
  outerOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
): (e: React.ChangeEvent<HTMLInputElement>) => void {
  return (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.target.value = sanitizeNumericValue(e.target.value, allowNegative, decimalPlaces)
    outerOnChange?.(e)
  }
}

function getSizeClass(size: 'sm' | 'md' | 'lg'): string {
  if (size === 'sm') { return 'el-input-sm' }
  if (size === 'lg') { return 'el-input-lg' }
  return 'el-input-md'
}

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  ({ className, size = 'md', hasError = false, allowNegative = false, decimalPlaces, hideMessage: _hideMessage, onKeyDown, onChange, ...props }, ref) => {
    return (
      <input
        ref={ref}
        className={clsx('el-input el-numeric-input', getSizeClass(size), hasError && 'el-input-invalid', className)}
        inputMode="decimal"
        type="text"
        onChange={buildNumericChangeHandler(allowNegative, decimalPlaces, onChange)}
        onKeyDown={buildNumericKeyDownHandler(allowNegative, onKeyDown)}
        {...props}
      />
    )
  }
)

NumericInput.displayName = 'NumericInput'

export default NumericInput
